import React from "react"
import { Localized } from "@fluent/react"
import { makeStyles } from "@material-ui/core/styles"
import { Box, Typography } from "@material-ui/core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBell } from "@fortawesome/pro-solid-svg-icons/faBell"

import { INotification, MessageTagValue } from "shared/Types/appTypes"
import { WellknownHeight } from "shared/Helpers/constants"
import { useToken } from "shared/Modules/Login/useToken"
import { useQuery } from "shared/Modules/Query/useQuery"
import { getNotifications } from "shared/Modules/Notification/notificationApi"
import Card from "shared/Components/Card/Card"
import Screen from "shared/Components/Skeleton/Screen"
import { useDateTime } from "shared/Modules/Localization/useDateTime"
import BottomNavBar from "../Components/Skeleton/BottomNavBar"
import { useEnvironment } from "shared/Modules/Environment/envHooks"
import { LocalizedEnum, LocalizedStrict } from "shared/Modules/Localization/Components/Localized"
import { EmptyContent } from "shared/Components/Skeleton/EmptyContent"
import { green, purple, red, yellow } from "@material-ui/core/colors"
import {
    faInfo,
    faCalendarExclamation,
    faBadgePercent,
    faPlateUtensils,
    faCalendar,
    faAlarmClock,
    faMessageSmile,
    faMegaphone,
    faHandHoldingHeart,
} from "@fortawesome/pro-regular-svg-icons"
import { faCircle } from "@fortawesome/pro-solid-svg-icons"
import { IconProp } from "@fortawesome/fontawesome-svg-core"

const useStyles = makeStyles((theme) => ({
    innerCard: {
        padding: "0.5rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.625rem",
    },
    timeIcon: {
        marginRight: 6,
        color: "#969696",
    },
    subject: {
        fontWeight: 500,
    },
    cardHeader: {
        display: "flex",
        alignItems: "center",
        gap: "0.625rem",
    },
    messageTime: {
        alignItems: "center",
        display: "flex",
        gap: "0.75rem",
    },
    colorBox: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "none",
        width: "1.5rem",
        height: "1.5rem",
        borderRadius: "100%",
        color: theme.palette.common.white,
        "& > svg": {
            fontSize: ".75rem",
        },
    },
    card: {
        boxShadow: "none !important",
        border: "1px solid #E2E8F0",
    },
    bottomSpace: {
        paddingBottom: `${WellknownHeight.BOTTOM_NAV_BAR + 24}px`,
    },
}))

export type MessageTagOption = Readonly<{
    value: MessageTagValue
    color: string
    icon: IconProp
}>

const options: MessageTagOption[] = [
    {
        value: MessageTagValue.NEWS,
        color: "#0284C7",
        icon: faMegaphone,
    },
    {
        value: MessageTagValue.INFO,
        color: "#0284C7",
        icon: faInfo,
    },
    {
        value: MessageTagValue.IMPORTANT,
        color: red[600],
        icon: faCalendarExclamation,
    },
    {
        value: MessageTagValue.OFFER,
        color: yellow[600],
        icon: faBadgePercent,
    },
    {
        value: MessageTagValue.EVENT,
        color: purple[600],
        icon: faCalendar,
    },
    {
        value: MessageTagValue.REMINDER,
        color: purple[600],
        icon: faAlarmClock,
    },
    {
        value: MessageTagValue.MENU_CHANGE,
        color: green[600],
        icon: faPlateUtensils,
    },
    {
        value: MessageTagValue.FEEDBACK,
        color: green[600],
        icon: faMessageSmile,
    },
    {
        value: MessageTagValue.HEALTH,
        color: green[600],
        icon: faHandHoldingHeart,
    },
]

type MessageTagIconProps = Readonly<{
    color: string
    icon: IconProp
    className?: string
}>

function MessageTagIcon({ color, icon, className }: MessageTagIconProps) {
    return (
        <Box className={className} style={{ backgroundColor: color }}>
            <FontAwesomeIcon icon={icon} />
        </Box>
    )
}

function Notifications() {
    const classes = useStyles()
    const token = useToken()
    const { currentEnv } = useEnvironment()
    const dateTimeFactory = useDateTime()

    const { QueryPane } = useQuery(() => getNotifications(token, currentEnv), [token, currentEnv])

    function renderNotifications(notifications: INotification[]) {
        if (notifications?.length > 0) {
            return (
                <Box className={classes.bottomSpace}>
                    {notifications.map((notification: INotification, index) => {
                        const time = dateTimeFactory.fromFNDateTime(notification.created)
                        const selectedOption = options.find((opt) => opt.value === notification.tag) ?? null

                        return (
                            <Card key={index} marginTop={20} cardClassName={classes.card}>
                                <Box className={classes.innerCard}>
                                    <Box className={classes.cardHeader}>
                                        {selectedOption && (
                                            <MessageTagIcon
                                                color={selectedOption.color}
                                                icon={selectedOption.icon}
                                                className={classes.colorBox}
                                            />
                                        )}
                                        <Typography variant="body1" className={classes.subject}>
                                            {notification.subject}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body1">{notification.message}</Typography>
                                    <Box className={classes.messageTime}>
                                        {notification.tag && (
                                            <>
                                                <LocalizedEnum
                                                    base="notifications-no-notifications-notification-time-type"
                                                    enumValue={notification.tag}
                                                >
                                                    <Typography color="textSecondary" variant="caption">
                                                        Nyhed
                                                    </Typography>
                                                </LocalizedEnum>
                                                <FontAwesomeIcon icon={faCircle} color="#94A3B8" width="5px" />
                                            </>
                                        )}
                                        <Typography variant="caption" color="textSecondary">
                                            {time.toRelative({ style: "narrow" })}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Card>
                        )
                    })}
                </Box>
            )
        }

        return (
            <LocalizedStrict id="notifications-no-notifications" attrs={{ title: true, description: true }}>
                <EmptyContent
                    icon={faBell}
                    backgroundColor="#ec4899" // Pink/500
                    title="No messages right now"
                    description="Here you will be able to read messages from the merchant and service center."
                />
            </LocalizedStrict>
        )
    }

    return (
        <Localized id="notifications-page-title" attrs={{ primaryTopBarTitle: true }}>
            <Screen
                name="Notifications"
                primaryTopBarTitle="Notifikationer"
                showPrimaryTopBar
                bottomElement={<BottomNavBar />}
                bottomElementHeight={WellknownHeight.BOTTOM_NAV_BAR}
                fitPage
            >
                <QueryPane centerVertical hasBottomNavBar>
                    {(data) => renderNotifications(data.notifications)}
                </QueryPane>
            </Screen>
        </Localized>
    )
}

export default Notifications
